import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Button, Modal, Tabs, message, Spin } from 'antd';
import type { TabsProps } from 'antd';
import { OPMApi } from '../../../../api/opmApi';
import type { ProcessFuncCodeDetailDto } from '../../../../api/opmApi';
import BasicInfoTab from './components/BasicInfoTab';
import ExecutionLogicTab from './components/ExecutionLogicTab';
import InputDefinitionTab from './components/InputDefinitionTab';
import OutputDefinitionTab from './components/OutputDefinitionTab';
import type { ProcessBasicInfo, ProcessDefinitionModalProps } from './types';
import type { SchemaField } from '../ProcessDefinition/types';
import {
  fieldsToInputSchema,
  fieldsToOutputSchema,
} from '../ProcessDefinition/types';

const ProcessDefinitionModal: React.FC<ProcessDefinitionModalProps> = ({
  visible,
  node,
  onClose,
  ontologyId,
}) => {
  const [loadingDefinition, setLoadingDefinition] = useState(false);
  const [definition, setDefinition] = useState<ProcessFuncCodeDetailDto | null>(
    null
  );

  // 基本信息优先使用当前节点快照，避免打开弹窗后标题和展示字段闪烁。
  const basicInfo = useMemo<ProcessBasicInfo | null>(() => {
    if (!node) return null;

    const mmRef = node.data('MMRef');
    const tags = Array.isArray(mmRef?.tags)
      ? mmRef.tags
      : Array.isArray(node.data('tags'))
        ? node.data('tags')
        : [];
    const updatedBy = Array.isArray(mmRef?.updatedBy) ? mmRef.updatedBy : [];

    return {
      ontologyId,
      processNodeId: mmRef?.id?.toString?.() || node.id() || '',
      name: node.data('label') || node.data('name') || '',
      codeName: mmRef?.codeName || node.data('codeName') || '',
      description: mmRef?.description || node.data('description') || '',
      tags,
      version: mmRef?.version || '',
      updatedAt: mmRef?.updatedAt || '',
      updatedBy,
    };
  }, [node, ontologyId]);

  // 流程定义数据状态
  const [processDefinition, setProcessDefinition] = useState<any>(null);
  const [definitionLoading, setDefinitionLoading] = useState(false);

  // Tab状态管理
  const [activeKey, setActiveKey] = useState<string>('basic');
  // 每次切换到执行逻辑 tab 时自增，通知子组件重新拉取最新接口定义并重新生成代码
  const [logicRefreshTrigger, setLogicRefreshTrigger] = useState(0);
  const [hasChanges, setHasChanges] = useState<{
    input: boolean;
    output: boolean;
  }>({ input: false, output: false });
  const [pendingTabKey, setPendingTabKey] = useState<string | null>(null);

  // 保存方法引用
  const inputSaveRef = useRef<(() => Promise<void>) | null>(null);
  const outputSaveRef = useRef<(() => Promise<void>) | null>(null);
  const inputTabRef = useRef<any>(null);
  const outputTabRef = useRef<any>(null);

  // 加载流程定义数据
  const loadProcessDefinition = useCallback(async () => {
    if (!basicInfo?.processNodeId || !ontologyId) return;

    setDefinitionLoading(true);
    try {
      const definition = await OPMApi.getProcessDefinition(
        ontologyId,
        basicInfo.processNodeId
      );
      setProcessDefinition(definition);
    } catch (error: any) {
      console.error('加载流程定义失败:', error);
      message.error('加载流程定义失败');
    } finally {
      setDefinitionLoading(false);
    }
  }, [basicInfo?.processNodeId, ontologyId]);

  // 当弹窗打开且有基础信息时加载定义数据
  useEffect(() => {
    if (visible && basicInfo?.processNodeId) {
      loadProcessDefinition();
    }
  }, [visible, basicInfo?.processNodeId, loadProcessDefinition]);

  // 设置保存方法引用
  useEffect(() => {
    console.log('设置保存方法引用:', {
      inputTabRef: !!inputTabRef.current,
      outputTabRef: !!outputTabRef.current,
    });
    if (inputTabRef.current) {
      inputSaveRef.current = () => {
        console.log('调用 inputSaveRef.current');
        return inputTabRef.current.save();
      };
    }
    if (outputTabRef.current) {
      outputSaveRef.current = () => {
        console.log('调用 outputSaveRef.current');
        return outputTabRef.current.save();
      };
    }
  }, [inputTabRef.current, outputTabRef.current]);

  // 检查是否有未保存的更改
  const checkHasChanges = useCallback(
    (type: 'input' | 'output') => {
      return hasChanges[type];
    },
    [hasChanges]
  );

  // 加载执行逻辑定义数据
  const loadExecutionLogicDefinition = useCallback(async () => {
    const processNodeId = basicInfo?.processNodeId;
    if (!processNodeId || !ontologyId) {
      setDefinition(null);
      return;
    }

    setLoadingDefinition(true);
    try {
      const result = await OPMApi.getProcessDefinition(
        ontologyId,
        processNodeId
      );
      setDefinition(result);
    } catch (error) {
      console.error('加载执行逻辑定义失败:', error);
      setDefinition(null);
    } finally {
      setLoadingDefinition(false);
    }
  }, [ontologyId, basicInfo?.processNodeId]);

  // 处理Tab切换前的检查
  const handleTabChange = useCallback(
    (targetKey: string) => {
      // 如果目标Tab和当前Tab相同，直接返回
      if (targetKey === activeKey) return;

      // 检查当前Tab是否有未保存的更改（仅针对input和output Tab）
      const currentType = activeKey as 'input' | 'output';
      if (
        (activeKey === 'input' || activeKey === 'output') &&
        checkHasChanges(currentType)
      ) {
        setPendingTabKey(targetKey);
        Modal.confirm({
          title: '未保存的更改',
          content: `当前${currentType === 'input' ? '输入' : '输出'}定义有未保存的更改，是否保存？`,
          okText: '保存',
          cancelText: '取消',
          onOk: async () => {
            // 保存成功后再切换Tab
            try {
              console.log(
                '开始保存，当前类型:',
                currentType,
                '目标Tab:',
                pendingTabKey
              );
              if (currentType === 'input' && inputSaveRef.current) {
                await inputSaveRef.current();
                console.log('input保存成功');
              } else if (currentType === 'output' && outputSaveRef.current) {
                await outputSaveRef.current();
                console.log('output保存成功');
              } else {
                message.info('请先点击保存按钮保存当前更改');
                setPendingTabKey(null);
                return;
              }
              console.log('保存完成，等待handleSaveDefinition处理切换');
            } catch (error) {
              console.error('保存失败:', error);
              setPendingTabKey(null);
            }
          },
          onCancel: () => {
            // 取消保存，直接切换Tab（此处 activeKey 已确定为 input|output，targetKey=logic 即需 refresh）
            if (targetKey === 'logic') {
              setLogicRefreshTrigger(prev => prev + 1);
            }
            setActiveKey(targetKey);
            setHasChanges(prev => ({ ...prev, [currentType]: false }));
            setPendingTabKey(null);
          },
        });
        return;
      }

      // 无更改或从basic/logic切换，直接切换
      if (targetKey === 'logic' && activeKey !== 'logic') {
        setLogicRefreshTrigger(prev => prev + 1);
      }
      setActiveKey(targetKey);
    },
    [activeKey, checkHasChanges]
  );

  // 统一的保存处理函数
  const handleSaveDefinition = useCallback(
    async (type: 'input' | 'output', data: SchemaField[]) => {
      console.log('=== handleSaveDefinition 被调用 ===');
      console.log('保存调试信息:', {
        type,
        dataLength: data?.length,
        basicInfo,
        processDefinition,
      });
      console.log('当前 pendingTabKey:', pendingTabKey);
      console.log('调用栈:', new Error().stack?.split('\n').slice(1, 5));

      if (!basicInfo?.processNodeId) {
        message.warning('缺少节点信息');
        return;
      }

      try {
        setDefinitionLoading(true);

        let updatedDefinition;
        console.log('updatedDefinition', type);

        if (type === 'input') {
          const newInputSchema = fieldsToInputSchema(data);
          updatedDefinition = await OPMApi.saveProcessInfo(
            ontologyId,
            basicInfo.processNodeId,
            {
              inputSchema: newInputSchema,
              outputSchema: processDefinition?.outputSchema || [],
            }
          );
        } else {
          const newOutputSchema = fieldsToOutputSchema(data);
          updatedDefinition = await OPMApi.saveProcessInfo(
            ontologyId,
            basicInfo.processNodeId,
            {
              inputSchema: processDefinition?.inputSchema || [],
              outputSchema: newOutputSchema,
            }
          );
        }

        // 更新状态
        setProcessDefinition(updatedDefinition);
        setHasChanges(prev => ({ ...prev, [type]: false }));
        message.success(`${type === 'input' ? '输入' : '输出'}定义保存成功`);

        // 如果有待切换的Tab，保存成功后切换
        setTimeout(() => {
          console.log('延迟检查 pendingTabKey:', pendingTabKey);
          if (pendingTabKey) {
            console.log('保存成功，切换到Tab:', pendingTabKey);
            if (pendingTabKey === 'logic') {
              setLogicRefreshTrigger(prev => prev + 1);
            }
            setActiveKey(pendingTabKey);
            setPendingTabKey(null);
          } else {
            console.log('保存成功，但无待切换Tab');
          }
        }, 0);
      } catch (error: any) {
        console.error('保存失败:', error);
        message.error(error.message || '保存失败');
      } finally {
        setDefinitionLoading(false);
      }
    },
    [processDefinition, basicInfo?.processNodeId, ontologyId, pendingTabKey]
  );

  const modalTitle = useMemo(() => {
    const nodeName = basicInfo?.name || 'Process';
    return `Process定义 - ${nodeName}`;
  }, [basicInfo]);

  const processNodeId = basicInfo?.processNodeId || '';

  // 弹窗打开时初始加载执行逻辑定义数据
  useEffect(() => {
    if (!visible || !processNodeId || !ontologyId) {
      setDefinition(null);
      setLoadingDefinition(false);
      return;
    }

    loadExecutionLogicDefinition();
  }, [visible, ontologyId, processNodeId, loadExecutionLogicDefinition]);

  const handleDefinitionUpdate = useCallback(
    (nextDefinition: ProcessFuncCodeDetailDto) => {
      setDefinition(nextDefinition);
    },
    []
  );

  const tabItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'basic',
        label: '基本信息',
        children: <BasicInfoTab info={basicInfo} />,
      },
      {
        key: 'input',
        label: '输入定义',
        children: (
          <Spin spinning={definitionLoading}>
            <InputDefinitionTab
              ref={inputTabRef}
              nodeId={basicInfo?.processNodeId}
              ontologyId={ontologyId}
              definition={processDefinition}
              onSave={data => handleSaveDefinition('input', data)}
              onHasChangesChange={hasChange =>
                setHasChanges(prev => ({ ...prev, input: hasChange }))
              }
            />
          </Spin>
        ),
      },
      {
        key: 'output',
        label: '输出定义',
        children: (
          <Spin spinning={definitionLoading}>
            <OutputDefinitionTab
              ref={outputTabRef}
              nodeId={basicInfo?.processNodeId}
              ontologyId={ontologyId}
              definition={processDefinition}
              onSave={data => handleSaveDefinition('output', data)}
              onHasChangesChange={hasChange =>
                setHasChanges(prev => ({ ...prev, output: hasChange }))
              }
            />
          </Spin>
        ),
      },
      {
        key: 'logic',
        label: '执行逻辑',
        children: (
          <ExecutionLogicTab
            ontologyId={ontologyId}
            processNodeId={processNodeId}
            definition={definition}
            loading={loadingDefinition}
            onDefinitionUpdate={handleDefinitionUpdate}
            refreshTrigger={logicRefreshTrigger}
          />
        ),
      },
    ],
    [
      basicInfo,
      definition,
      definitionLoading,
      handleDefinitionUpdate,
      handleSaveDefinition,
      loadingDefinition,
      logicRefreshTrigger,
      ontologyId,
      processDefinition,
      processNodeId,
    ]
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      width={960}
      zIndex={1200}
      destroyOnClose
      maskClosable={false}
      footer={null}
      styles={{
        body: { paddingTop: 12 },
      }}
    >
      <div
        onKeyDown={event => {
          const isCopyPaste =
            (event.ctrlKey || event.metaKey) &&
            ['c', 'v', 'x'].includes(event.key);
          const isUndo = (event.ctrlKey || event.metaKey) && event.key === 'z';
          const isRedo = (event.ctrlKey || event.metaKey) && event.key === 'y';
          const isSelectAll =
            (event.ctrlKey || event.metaKey) && event.key === 'a';
          const isSave = (event.ctrlKey || event.metaKey) && event.key === 's';

          if (isCopyPaste || isUndo || isRedo || isSelectAll || isSave) {
            event.stopPropagation();
          }

          if (isSave) {
            event.preventDefault();
          }
        }}
      >
        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          items={tabItems}
        />
      </div>
    </Modal>
  );
};

export default ProcessDefinitionModal;
